import React, { useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** 단일 카탈로그 — 백엔드 seed-final / dataMonth=3 */
const DATA_MONTH = 3;

function getPreviousMonth() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  let prevYear = year;
  let prevMonth = month - 1;
  if (prevMonth === 0) {
    prevMonth = 12;
    prevYear = year - 1;
  }
  return {
    year: prevYear,
    month: prevMonth.toString().padStart(2, '0')
  };
}

function buildRankUrls() {
  const { year, month } = getPreviousMonth();
  const q = `?dataMonth=${encodeURIComponent(String(DATA_MONTH))}`;
  return {
    listUrl: `${API_URL}/rank/loan-rank/${year}/${month}${q}`,
    imagesUrl: `${API_URL}/rank/with-images/${year}/${month}${q}`
  };
}

/** test-eco-bookcard-ai-front 와 동일 — GS 백엔드 /rank/with-images 가 eco 형식(JSON)으로 응답 */
function transformBookData(book) {
  return {
    title: book.bookname,
    author: book.authors,
    publisher: book.publisher,
    coverUrl: book.bookImageURL,
    imageUrl: book.imageUrl || [],
    age: book.age,
    isbn13: book.isbn13,
    bookRank: book.bookRank,
    month: book.month,
    publicationYear: book.publicationYear,
    coverYN: book.coverYN,
    bookname: book.bookname,
    loanRank: book.bookRank
  };
}

const BASE_BOOK_W = 300;
/** 표지 카드 세로(캐러셀 슬라이드 높이와 무관 — 레이아웃만 별도) */
const BASE_BOOK_H = 370;
const BASE_IMG = 328;
const BASE_GAP = 18;
const STACK_MARGIN = 12;
const CAROUSEL_VISIBLE_SLIDES = 1.14;
const CAROUSEL_NATURAL =
  CAROUSEL_VISIBLE_SLIDES * BASE_IMG +
  Math.floor(CAROUSEL_VISIBLE_SLIDES) * BASE_GAP;
const SLIDE_NATURAL_ROW = BASE_BOOK_W + STACK_MARGIN + CAROUSEL_NATURAL;
const SLIDE_COMPACT_BREAKPOINT = 719;

function App() {
  const [bookData, setBookData] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rankList, setRankList] = useState([]);
  const [allBooks, setAllBooks] = useState([]);

  const imageInterval = useRef(null);
  const [loading, setLoading] = useState(true);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipRef = useRef(null);
  const iconRef = useRef(null);
  const dropdownBtnRef = useRef(null);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const mainRef = useRef(null);
  const [slideLayout, setSlideLayout] = useState(() => ({
    compact: false,
    scale: 1,
    bookW: BASE_BOOK_W,
    bookH: BASE_BOOK_H,
    imgSize: BASE_IMG,
    slideGap: BASE_GAP,
    carouselW: CAROUSEL_NATURAL,
    stackMargin: STACK_MARGIN
  }));

  const measureSlideLayout = useCallback(() => {
    const el = mainRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    const padX =
      (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const compact = el.clientWidth <= SLIDE_COMPACT_BREAKPOINT;
    const arrowSpace = compact ? 24 : el.clientWidth < 560 ? 28 : 52;
    const avail = Math.max(200, el.clientWidth - padX - arrowSpace);

    if (compact) {
      const scale = Math.min(1, avail / SLIDE_NATURAL_ROW);
      const bookW = BASE_BOOK_W * scale;
      const bookH = BASE_BOOK_H * scale;
      const imgSize = BASE_IMG * scale;
      const slideGap = BASE_GAP * scale;
      const carouselW = CAROUSEL_NATURAL * scale;
      const stackMargin = STACK_MARGIN * scale;
      setSlideLayout({
        compact: true,
        scale,
        bookW,
        bookH,
        imgSize,
        slideGap,
        carouselW,
        stackMargin
      });
      return;
    }

    const scaleFit = Math.min(1, avail / SLIDE_NATURAL_ROW);
    const scale = scaleFit;
    const bookW = BASE_BOOK_W * scale;
    const bookH = BASE_BOOK_H * scale;
    const imgSize = BASE_IMG * scale;
    const slideGap = BASE_GAP * scale;
    const carouselW = CAROUSEL_NATURAL * scale;
    const stackMargin = STACK_MARGIN * scale;
    setSlideLayout({
      compact: false,
      scale,
      bookW,
      bookH,
      imgSize,
      slideGap,
      carouselW,
      stackMargin
    });
  }, []);

  useLayoutEffect(() => {
    measureSlideLayout();
    const el = mainRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measureSlideLayout());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureSlideLayout, loading, bookData]);

  const images = bookData?.imageUrl || [];
  const imageCount = images.length;

  const extendedImages = [...images, ...images, ...images];

  const startIdx = imageCount;

  const realIdx = startIdx + currentImage;

  useEffect(() => {
    const fetchInit = async () => {
      setLoading(true);
      setRankList([]);
      setBookData(null);
      setCurrentImage(0);
      try {
        const { listUrl, imagesUrl } = buildRankUrls();

        const listRes = await fetch(listUrl);
        const listData = await listRes.json();
        if (Array.isArray(listData)) {
          const transformedList = listData.slice(0, 50).map((book) => transformBookData(book));
          setRankList(transformedList);
        } else {
          setRankList([]);
        }

        const imgRes = await fetch(imagesUrl);
        const imgData = await imgRes.json();

        if (Array.isArray(imgData) && imgData.length > 0) {
          const randomIdx = Math.floor(Math.random() * imgData.length);
          const initialBook = imgData[randomIdx];
          const transformed = transformBookData(initialBook);
          setBookData(transformed);
          setAllBooks(imgData);
          window.__WITH_IMAGES_DATA__ = imgData;
        } else {
          setAllBooks([]);
          window.__WITH_IMAGES_DATA__ = [];
        }
      } catch (err) {
        console.error('Initial fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInit();
  }, []);

  useEffect(() => {
    if (bookData && bookData.imageUrl && bookData.imageUrl.length > 0) {
      imageInterval.current = setInterval(() => {
        next();
      }, 3500);
      return () => clearInterval(imageInterval.current);
    }
  }, [bookData, currentImage]);

  const prev = () => {
    setIsTransitioning(true);
    setCurrentImage((prev) => prev - 1);
  };
  const next = () => {
    setIsTransitioning(true);
    setCurrentImage((prev) => prev + 1);
  };

  const switchToRandomBook = useCallback(() => {
    if (allBooks.length <= 1) return;

    // 현재 도서를 제외한 랜덤 도서 선택
    let nextIdx;
    do {
      nextIdx = Math.floor(Math.random() * allBooks.length);
    } while (allBooks[nextIdx].isbn13 === bookData.isbn13);

    const nextBook = allBooks[nextIdx];
    setBookData(transformBookData(nextBook));
  }, [allBooks, bookData?.isbn13]);

  const handleTransitionEnd = () => {
    if (imageCount <= 0) return;
    if (currentImage >= imageCount) {
      setIsTransitioning(false);
      setCurrentImage(0);
      switchToRandomBook();
    }
    if (currentImage < 0) {
      setIsTransitioning(false);
      setCurrentImage(imageCount - 1);
    }
  };

  const getAgeTag = (age) => {
    const explain = (label) => (
      <span className="ai-tooltip-wrap ai-tooltip-wrap--age">
        <span className="ai-slide-footer-tag green">
          <span className="age-star" aria-hidden="true">
            ★
          </span>
          {label}
          <span className="ai-slide-tag-q" tabIndex={0} aria-label="분류 기준 안내">
            ?
          </span>
        </span>
        <div className="ai-tooltip-box ai-tooltip-box--age">
          대출 데이터에서, 해당 연령대로 분류된 이용자들이 많이 대출한 도서를 의미합니다.
        </div>
      </span>
    );
    switch (age) {
      case 'children':
        return explain(<><b className="age-em">어린이</b>가 많이 읽은 도서</>);
      case 'elementary':
        return explain(<><b className="age-em">초등학생</b>이 많이 읽은 도서</>);
      case 'middle':
        return explain(<><b className="age-em">중고등학생</b>이 많이 읽은 도서</>);
      case 'age20s30s':
        return explain(<><b className="age-em">20~30대</b>가 많이 읽은 도서</>);
      case 'age40s50s':
        return explain(<><b className="age-em">40~50대</b>가 많이 읽은 도서</>);
      case 'age60plus':
        return explain(<><b className="age-em">60대</b>가 많이 읽은 도서</>);
      default:
        return null;
    }
  };

  const handleDropdownItemClick = (isbn13, age) => {
    setDropdownOpen(false);
    setCurrentImage(0);

    // with-images 데이터에서 매칭되는 이미지 데이터 찾기
    const imagesData = window.__WITH_IMAGES_DATA__ || [];
    const matchedBook = imagesData.find(b => b.isbn13 === isbn13);

    if (matchedBook) {
      setBookData(transformBookData(matchedBook));
    } else {
      const selectedBook = rankList.find((book) => book.isbn13 === isbn13);
      if (selectedBook) {
        setBookData({
          ...selectedBook,
          age: age || selectedBook.age
        });
      }
    }
  };

  const SkeletonMain = () => (
    <div className="ai-slide-main" ref={mainRef}>
      <div className="ai-skeleton-bookcard" />
      <div className="ai-skeleton-carousel">
        <div className="ai-skeleton-img" />
        <div className="ai-skeleton-img" />
        <div className="ai-skeleton-img" />
      </div>
    </div>
  );
  const SkeletonFooter = () => (
    <div className="ai-slide-footer">
      <div className="ai-skeleton-footer-info" />
      <div className="ai-skeleton-footer-tags">
        <div className="ai-skeleton-footer-tag" />
        <div className="ai-skeleton-footer-tag" />
        <div className="ai-skeleton-footer-tag" />
      </div>
    </div>
  );

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e) => {
      if (tooltipRef.current && tooltipRef.current.contains(e.target)) return;
      if (iconRef.current && iconRef.current.contains(e.target)) return;
      if (dropdownBtnRef.current && dropdownBtnRef.current.contains(e.target)) return;
      const dropdown = document.querySelector('.ai-slide-dropdown');
      if (dropdown && dropdown.contains(e.target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  const renderCarouselStrip = (extraClass) => {
    if (!imageCount) return null;
    return (
    <div
      className={`ai-slide-carousel-outer ${extraClass}`}
      style={{
        width: slideLayout.carouselW,
        height: slideLayout.imgSize,
        flexShrink: 1,
        minWidth: 0
      }}
    >
      <div
        className="ai-slide-carousel-track"
        style={{
          height: slideLayout.imgSize,
          transform: `translateX(-${realIdx * (slideLayout.imgSize + slideLayout.slideGap)}px)`,
          transition: isTransitioning ? 'transform 0.6s cubic-bezier(0.4,0,0.2,1)' : 'none'
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {extendedImages.map((img, idx) => (
          <img
            key={idx + img}
            src={img}
            alt={`북카드 이미지${idx % imageCount}`}
            className="ai-slide-img"
            width={Math.round(slideLayout.imgSize)}
            height={Math.round(slideLayout.imgSize)}
            style={{
              width: slideLayout.imgSize,
              height: slideLayout.imgSize,
              marginRight: slideLayout.slideGap,
              flexShrink: 0
            }}
            draggable={false}
          />
        ))}
      </div>
      <div className="ai-slide-carousel-fade" />
    </div>
    );
  };

  return (
    <>
      <div className="ai-slide-root">
        <div className="ai-slide-header">
          <div className="ai-slide-header-main">
            <div className="ai-slide-header-row1">
              <span className="ai-slide-title">AI추천 카드 콘텐츠</span>
            </div>
            <div className="ai-slide-dropdown-wrap">
              <button
                className="ai-slide-dropdown-btn"
                ref={dropdownBtnRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen((v) => !v);
                }}
              >
                더보기 <span className="arrow">▼</span>
              </button>
              {dropdownOpen && (
                <div className="ai-slide-dropdown">
                  <div className="ai-slide-dropdown-title">
                    AI 카드 콘텐츠 목록
                    <div className="ai-tooltip-wrap">
                      <svg
                        viewBox="0 0 24 24"
                        ref={iconRef}
                        onMouseEnter={() => setTooltipOpen(true)}
                        onMouseLeave={() => setTooltipOpen(false)}
                        style={{ width: '1.2em', height: '1.2em', verticalAlign: 'middle', cursor: 'pointer', fill: 'currentColor', marginLeft: '4px', pointerEvents: 'all' }}
                      >
                        <path d="M0 0h24v24H0V0z" fill="none" /><path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z" />
                      </svg>
                      {tooltipOpen && (
                        <div className="ai-tooltip-box" ref={tooltipRef} style={{ display: 'block' }}>
                          도서명을 클릭하면 해당 도서의 AI카드 콘텐츠를 보실 수 있습니다.
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="ai-slide-dropdown-list">
                    {rankList.map((item, idx) => (
                      <div
                        key={item.isbn13}
                        className="ai-slide-dropdown-item ellipsis"
                        onClick={() => handleDropdownItemClick(item.isbn13, item.age)}
                        style={{ cursor: 'pointer' }}
                        title={item.bookname}
                      >
                        <span className="rank-num">{idx + 1}</span> {item.bookname}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <span className="ai-slide-desc">
              아래의 북카드 콘텐츠는 <span className="ai-slide-ai">AI가 생성하였습니다.</span>
            </span>
          </div>
        </div>
        {loading ? (
          /* 스켈레톤 메인 로더 */
          <div className="ai-slide-main" style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="ai-slide-spread-row">
              <div className="ai-slide-bookcard-stack" style={{ width: 300 }}>
                <div className="ai-slide-bookcard ai-slide-bookcard--split skeleton" style={{ height: 370, opacity: 0.6 }}>
                  <div className="ai-slide-bookcard-media skeleton" style={{ height: '70%', background: '#e5e7eb' }} />
                  <div className="ai-slide-bookcard-caption">
                    <div className="skeleton" style={{ height: '20px', width: '80%', marginBottom: '10px' }} />
                    <div className="skeleton" style={{ height: '14px', width: '50%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : !bookData ? (
          <div
            className="ai-slide-main ai-slide-main--empty"
            style={{
              minHeight: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '1rem',
              padding: '0 24px',
              textAlign: 'center'
            }}
          >
            표시할 도서가 없습니다. 백엔드 API와 DB(bookcard-final)를 확인하세요.
          </div>
        ) : (
          <div className="ai-slide-main" ref={mainRef}>
            <div className="ai-slide-arrow-wrap left" aria-hidden="false">
              <div className="ai-tooltip-wrap ai-tooltip-wrap--arrow ai-tooltip-wrap--arrow-left">
                <button className="ai-slide-arrow" onClick={prev} aria-label="이전 이미지">
                  &lt;
                </button>
                <div className="ai-tooltip-box ai-tooltip-box--arrow">
                  이전 이미지로 이동
                </div>
              </div>
            </div>
            {!slideLayout.compact ? (
              <div className="ai-slide-spread-row">
                <div
                  className="ai-slide-bookcard-back"
                  aria-hidden="true"
                  style={{
                    width: slideLayout.bookW,
                    height: slideLayout.bookH
                  }}
                />
                <div
                  className="ai-slide-bookcard-stack"
                  style={{
                    width: slideLayout.bookW,
                    height: slideLayout.bookH,
                    marginRight: slideLayout.stackMargin,
                    flexShrink: 0
                  }}
                >
                  <div className="ai-slide-bookcard ai-slide-bookcard--split">
                    <div className="ai-slide-bookcard-media">
                      {bookData.coverUrl && (
                        <img
                          src={bookData.coverUrl}
                          alt="book visual"
                          className="ai-slide-bookcard-bgimg"
                        />
                      )}
                    </div>
                    <div className="ai-slide-bookcard-caption">
                      <div className="ai-slide-book-title">{bookData.title}</div>
                      <div className="ai-slide-book-bottom">
                        <span className="ai-slide-book-author">{bookData.author}</span>
                        <span className="ai-slide-book-publisher">
                          {' '}
                          | {bookData.publisher}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {renderCarouselStrip('ai-slide-carousel-outer--paired')}
              </div>
            ) : (
              <div className="ai-slide-spread-row ai-slide-spread-row--compact">
                <div
                  className="ai-slide-bookcard-stack ai-slide-bookcard-stack--compact"
                  style={{
                    width: slideLayout.bookW,
                    height: slideLayout.bookH,
                    marginRight: slideLayout.stackMargin,
                    flexShrink: 0
                  }}
                >
                  <div className="ai-slide-bookcard ai-slide-bookcard--split">
                    <div className="ai-slide-bookcard-media">
                      {bookData.coverUrl && (
                        <img
                          src={bookData.coverUrl}
                          alt="book visual"
                          className="ai-slide-bookcard-bgimg"
                        />
                      )}
                    </div>
                    <div className="ai-slide-bookcard-caption">
                      <div className="ai-slide-book-title">{bookData.title}</div>
                      <div className="ai-slide-book-bottom">
                        <span className="ai-slide-book-author">{bookData.author}</span>
                        <span className="ai-slide-book-publisher">
                          {' '}
                          | {bookData.publisher}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {renderCarouselStrip('ai-slide-carousel-outer--paired')}
              </div>
            )}
            <div className="ai-slide-arrow-wrap right" aria-hidden="false">
              <div className="ai-tooltip-wrap ai-tooltip-wrap--arrow ai-tooltip-wrap--arrow-right">
                <button className="ai-slide-arrow" onClick={next} aria-label="다음 이미지">
                  &gt;
                </button>
                <div className="ai-tooltip-box ai-tooltip-box--arrow">
                  다음 이미지로 이동
                </div>
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="ai-slide-footer skeleton" style={{ height: '60px', marginTop: '20px', opacity: 0.5 }} />
        ) : !bookData ? (
          <div className="ai-slide-footer skeleton" style={{ height: '60px', marginTop: '20px', opacity: 0.35 }} />
        ) : (
          <div className="ai-slide-footer">
            <div className="ai-slide-footer-info">
              <span className="ai-slide-footer-label">카드 콘텐츠 소개 도서</span>
              <div className="ai-slide-meta">
                <div className="ai-slide-meta-row">
                  <span className="ai-slide-meta-k">도서명</span>
                  <span className="ai-slide-meta-v ai-slide-footer-title">{bookData.title}</span>
                </div>
                <div className="ai-slide-meta-row">
                  <span className="ai-slide-meta-k">저자</span>
                  <span className="ai-slide-meta-v">{bookData.author || '-'}</span>
                  <span className="ai-slide-meta-sep">|</span>
                  <span className="ai-slide-meta-k">출판사</span>
                  <span className="ai-slide-meta-v">{bookData.publisher || '-'}</span>
                </div>
              </div>
            </div>
            <div className="ai-slide-footer-tags">{getAgeTag(bookData.age)}</div>
          </div>
        )}
      </div>
      <div className="ai-slide-root-note">
        좌측은 도서 표지 이미지, 우측은 AI 카드 이미지입니다.
      </div>
    </>
  );
}

export default App;
